<script setup lang="ts">
  import RichTextEditor from '@/shared/components/RichTextEditor.vue'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import { PlusIcon, SparklesIcon } from '@heroicons/vue/24/outline'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import { computed, ref } from 'vue'
  import type { ClientData, OpponentData, CustomerCase as Case } from '@/types/generated'
  import InputField from '@/components/admin/form/InputField.vue'
  import AutoSavingForm from '@/components/admin/form/AutoSavingForm.vue'
  import { Field } from 'vee-validate'

  const caseStore = useCaseStore()
  const caseRecord = computed<Case | null>(() => caseStore.caseRecord)
  type Party = {
    name: string
    data: ClientData | OpponentData
    handleSave: (values: any) => void
  }
  const parties = computed<Party[]>(() => {
    return [
      {
        name: 'Mandant',
        data: caseRecord.value?.data.client || [],
        handleSave: (values) => {
          console.log('Mandant saved', values)
        }
      },
      {
        name: 'Gegner',
        data: caseRecord.value?.data.opponent || [],
        handleSave: (values) => {
          console.log('Gegner saved', values)
        }
      }
    ]
  })
  const selectedParty = ref<Party>(parties.value[0])

  const handleSave = (values: any) => {
    console.log('AutoSavingForm save event', values)
  }
</script>

<template>
  <div class="flex gap-4">
    <!-- LEFT SIDE (Form and Info Cards) -->
    <div class="flex w-1/3 flex-col gap-5">
      <!-- SelectFields Section -->
      <div class="flex flex-1 flex-col rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <SelectField
          :options="[]"
          name=""
          label="Sachbearbeiter"
        />
        <SelectField
          :options="[]"
          name=""
          label="Rechtsgebiet"
        />
        <SelectField
          :options="[]"
          name=""
          label="Rechtsthema"
        />
      </div>
      <!-- Static Text Info Card -->
      <div class="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        This is some text within a card body.
      </div>
    </div>

    <!-- RIGHT SIDE (Rich Text Editor Card) -->
    <div class="w-2/3">
      <div class="flex h-full flex-col rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <!-- Label outside the editor to not interfere with flex growth -->
        <div class="mb-4 flex items-center justify-between">
          <div class="text-lg font-semibold text-gray-800">Inhalt der Anfrage</div>
          <button
            class="inline-flex items-center gap-x-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm
              font-semibold text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500
              disabled:pointer-events-none disabled:opacity-50"
          >
            <SparklesIcon class="h-5 w-5" />
            Mit KI zusammenfassen
          </button>
        </div>
        <!-- RichTextEditor container with flex-1 to stretch height -->
        <div class="flex-1">
          <RichTextEditor
            model-value="Hello, World!"
            :fixed-height="true"
          />
        </div>
      </div>
    </div>
  </div>

  <!-- Another card section -->
  <div>
    <div class="mt-10 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <div class="flex gap-5">
        <div class="w-1/2 border-r-2 pr-4">
          <div class="flex items-center justify-between">
            <div class="text-lg font-semibold text-gray-800">Beteiligte</div>
            <button
              class="inline-flex items-center gap-x-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm
                font-semibold text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500
                disabled:pointer-events-none disabled:opacity-50"
            >
              <PlusIcon class="h-5 w-5" />
              Neuen Beteiligten hinzufügen
            </button>
          </div>
          <div class="mt-4">
            <div
              v-for="party in parties"
              :key="party.name"
            >
              <div class="text-sm font-semibold text-gray-800">{{ party.name }}</div>
              <div class="text-sm text-gray-600">
                {{ party.data.first_name }} {{ party.data.last_name }}
              </div>
            </div>
          </div>
        </div>
        <div class="w-1/2">
          <AutoSavingForm @save="selectedParty.handleSave">
            <div
              class="mb-4 grid w-full grid-cols-2 items-center justify-center rounded-md bg-muted p-1
                text-muted-foreground"
            >
              <Field
                v-slot="{ errorMessage }"
                v-model="selectedParty.data.is_company"
                name="is_company"
              >
                <p v-if="errorMessage">{{ errorMessage }}</p>
                <!-- 'Natürliche Person' button: Sets value to false -->
                <button
                  type="button"
                  class="inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium
                    ring-offset-background transition-all disabled:pointer-events-none disabled:opacity-50"
                  :class="
                    !selectedParty.data.is_company ? 'bg-background text-foreground shadow-sm' : ''
                  "
                  @click="selectedParty.data.is_company = false"
                >
                  <span class="truncate">Natürliche Person</span>
                </button>

                <!-- 'Juristische Person' button: Sets value to true -->
                <button
                  type="button"
                  class="inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium
                    ring-offset-background transition-all disabled:pointer-events-none disabled:opacity-50"
                  :class="
                    selectedParty.data.is_company ? 'bg-background text-foreground shadow-sm' : ''
                  "
                  @click="selectedParty.data.is_company = true"
                >
                  <span class="truncate">Juristische Person</span>
                </button>
              </Field>
            </div>
            <div>
              <InputField
                v-model="selectedParty.data.first_name"
                name="first_name"
                label="Vorname"
              />
            </div>
          </AutoSavingForm>
        </div>
      </div>
    </div>
  </div>
</template>
